import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  CreditCard,
  Check,
  Clock,
  AlertCircle,
  Shield,
  Zap,
  Building,
  Calendar,
  Users,
  ArrowRight,
  RefreshCw,
  CheckCircle2,
  XCircle,
  Loader2,
  ExternalLink,
  FileText,
  Download,
  Settings,
  Receipt
} from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { useToast } from '../hooks/use-toast';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BillingPage = () => {
  const [subscription, setSubscription] = useState(null);
  const [plans, setPlans] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    fetchSubscription();
    fetchPlans();
    fetchInvoices();
    fetchPaymentMethods();
    
    // Check if returning from Stripe checkout
    const sessionId = searchParams.get('session_id');
    const status = searchParams.get('status');
    
    if (sessionId && status === 'success') {
      pollPaymentStatus(sessionId);
    }
  }, [searchParams]);

  const fetchSubscription = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await axios.get(`${API_URL}/api/subscriptions/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscription(response.data);
    } catch (error) {
      console.error('Error fetching subscription:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPlans = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/subscriptions/plans`);
      setPlans(response.data);
    } catch (error) {
      console.error('Error fetching plans:', error);
    }
  };

  const fetchInvoices = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await axios.get(`${API_URL}/api/subscriptions/invoices`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInvoices(response.data.invoices || []);
    } catch (error) {
      console.error('Error fetching invoices:', error);
    }
  };

  const fetchPaymentMethods = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await axios.get(`${API_URL}/api/subscriptions/payment-methods`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPaymentMethods(response.data.payment_methods || []);
    } catch (error) {
      console.error('Error fetching payment methods:', error);
    }
  };

  const openCustomerPortal = async () => {
    setPortalLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await axios.post(
        `${API_URL}/api/subscriptions/customer-portal`,
        { return_url: window.location.href },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Redirect to Stripe Customer Portal
      window.location.href = response.data.portal_url;
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to open billing portal. Please try again.",
        variant: "destructive"
      });
      setPortalLoading(false);
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      toast({
        title: "Payment Status",
        description: "Payment verification timed out. Please refresh the page.",
        variant: "default"
      });
      setCheckingPayment(false);
      return;
    }

    setCheckingPayment(true);

    try {
      const token = localStorage.getItem('auth_token');
      const response = await axios.get(
        `${API_URL}/api/subscriptions/checkout/status/${sessionId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.payment_status === 'paid') {
        toast({
          title: "Payment Successful!",
          description: "Your subscription is now active. Welcome to WSafe AI!",
          variant: "default"
        });
        setCheckingPayment(false);
        fetchSubscription();
        // Clear URL params
        navigate('/billing', { replace: true });
        return;
      } else if (response.data.status === 'expired') {
        toast({
          title: "Session Expired",
          description: "Your checkout session expired. Please try again.",
          variant: "destructive"
        });
        setCheckingPayment(false);
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment:', error);
      setCheckingPayment(false);
    }
  };

  const handleCancelSubscription = async () => {
    setCancelLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      await axios.post(
        `${API_URL}/api/subscriptions/cancel`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast({
        title: "Subscription Canceled",
        description: "Your subscription has been canceled. You can continue using the service until the end of your billing period.",
        variant: "default"
      });
      
      setShowCancelDialog(false);
      fetchSubscription();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to cancel subscription. Please try again.",
        variant: "destructive"
      });
    } finally {
      setCancelLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      trialing: { color: 'bg-blue-100 text-blue-700', icon: Clock, label: 'Free Trial' },
      active: { color: 'bg-green-100 text-green-700', icon: CheckCircle2, label: 'Active' },
      past_due: { color: 'bg-yellow-100 text-yellow-700', icon: AlertCircle, label: 'Past Due' },
      canceled: { color: 'bg-red-100 text-red-700', icon: XCircle, label: 'Canceled' },
      none: { color: 'bg-gray-100 text-gray-700', icon: AlertCircle, label: 'No Subscription' }
    };
    
    const badge = badges[status] || badges.none;
    const Icon = badge.icon;
    
    return (
      <Badge className={badge.color}>
        <Icon className="w-3 h-3 mr-1" />
        {badge.label}
      </Badge>
    );
  };

  const getPlanIcon = (planId) => {
    if (planId?.includes('starter')) return Shield;
    if (planId?.includes('professional')) return Zap;
    if (planId?.includes('enterprise')) return Building;
    return Shield;
  };

  const getTrialDaysRemaining = () => {
    if (!subscription?.trial_ends_at) return 0;
    const trialEnd = new Date(subscription.trial_ends_at);
    const now = new Date();
    const diff = trialEnd - now;
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  };

  if (loading || checkingPayment) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-4 md:p-8">
        <div className="max-w-4xl mx-auto flex flex-col items-center justify-center py-20">
          <Loader2 className="w-12 h-12 animate-spin text-blue-600 mb-4" />
          <p className="text-slate-600">
            {checkingPayment ? 'Verifying your payment...' : 'Loading billing information...'}
          </p>
        </div>
      </div>
    );
  }

  const PlanIcon = subscription?.plan_id ? getPlanIcon(subscription.plan_id) : Shield;
  const trialDays = getTrialDaysRemaining();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-4 md:p-8">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Billing & Subscription</h1>
            <p className="text-slate-500 mt-1">Manage your subscription and billing details</p>
          </div>
          <Button
            variant="outline"
            onClick={fetchSubscription}
            className="gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </Button>
        </div>

        {/* Current Plan Card */}
        <Card className="border-t-4 border-t-blue-500">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
                  <PlanIcon className="w-7 h-7 text-white" />
                </div>
                <div>
                  <CardTitle className="text-xl">
                    {subscription?.plan_name || 'No Active Plan'}
                  </CardTitle>
                  <CardDescription>
                    {subscription?.has_subscription 
                      ? `${subscription.employee_limit ? `Up to ${subscription.employee_limit} employees` : 'Unlimited employees'}`
                      : 'Start your free trial today'}
                  </CardDescription>
                </div>
              </div>
              {getStatusBadge(subscription?.status || 'none')}
            </div>
          </CardHeader>
          
          <CardContent className="space-y-6">
            {subscription?.has_subscription ? (
              <>
                {/* Trial Progress */}
                {subscription.status === 'trialing' && (
                  <div className="bg-blue-50 rounded-xl p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-blue-900">Free Trial</span>
                      <span className="text-sm text-blue-700">{trialDays} days remaining</span>
                    </div>
                    <Progress value={((14 - trialDays) / 14) * 100} className="h-2" />
                    <p className="text-xs text-blue-600">
                      Your trial ends on {new Date(subscription.trial_ends_at).toLocaleDateString()}
                    </p>
                  </div>
                )}

                {/* Billing Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-slate-50 rounded-xl p-4">
                    <div className="flex items-center gap-2 text-slate-500 mb-1">
                      <Calendar className="w-4 h-4" />
                      <span className="text-sm">Next Billing Date</span>
                    </div>
                    <p className="font-semibold text-slate-900">
                      {subscription.current_period_end 
                        ? new Date(subscription.current_period_end).toLocaleDateString()
                        : 'N/A'}
                    </p>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-4">
                    <div className="flex items-center gap-2 text-slate-500 mb-1">
                      <Users className="w-4 h-4" />
                      <span className="text-sm">Employee Limit</span>
                    </div>
                    <p className="font-semibold text-slate-900">
                      {subscription.employee_limit || 'Unlimited'}
                    </p>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex flex-wrap gap-3">
                  <Button 
                    variant="outline" 
                    onClick={openCustomerPortal}
                    disabled={portalLoading}
                    className="flex-1"
                  >
                    {portalLoading ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Settings className="w-4 h-4 mr-2" />
                    )}
                    Manage Billing
                    <ExternalLink className="w-3 h-3 ml-2" />
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={() => navigate('/pricing')}
                  >
                    Change Plan
                  </Button>
                  {subscription.status !== 'canceled' && (
                    <Button 
                      variant="outline" 
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={() => setShowCancelDialog(true)}
                    >
                      Cancel
                    </Button>
                  )}
                </div>
              </>
            ) : (
              /* No Subscription CTA */
              <div className="text-center py-8">
                <div className="w-20 h-20 mx-auto rounded-full bg-blue-100 flex items-center justify-center mb-4">
                  <CreditCard className="w-10 h-10 text-blue-600" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2">
                  Start Your Free Trial
                </h3>
                <p className="text-slate-500 mb-6 max-w-md mx-auto">
                  Get full access to WSafe AI for 14 days. No credit card required. Cancel anytime.
                </p>
                <Button 
                  size="lg"
                  className="bg-blue-600 hover:bg-blue-700"
                  onClick={() => navigate('/pricing')}
                >
                  View Plans & Start Trial
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Billing Details Tabs - Only show if has subscription */}
        {subscription?.has_subscription && (
          <Tabs defaultValue="invoices" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="invoices" className="gap-2">
                <Receipt className="w-4 h-4" />
                Invoices
              </TabsTrigger>
              <TabsTrigger value="payment-methods" className="gap-2">
                <CreditCard className="w-4 h-4" />
                Payment Methods
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="invoices" className="mt-4">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-lg">Invoice History</CardTitle>
                      <CardDescription>View and download your past invoices</CardDescription>
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={openCustomerPortal}
                      disabled={portalLoading}
                    >
                      <ExternalLink className="w-4 h-4 mr-2" />
                      View All in Portal
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {invoices.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      <FileText className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                      <p>No invoices yet</p>
                      <p className="text-sm">Invoices will appear here after your first payment</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {invoices.map((invoice) => (
                        <div 
                          key={invoice.id}
                          className="flex items-center justify-between p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors"
                        >
                          <div className="flex items-center gap-4">
                            <div className="w-10 h-10 rounded-lg bg-white border flex items-center justify-center">
                              <FileText className="w-5 h-5 text-slate-500" />
                            </div>
                            <div>
                              <p className="font-medium text-slate-900">
                                Invoice {invoice.number || invoice.id.slice(-8)}
                              </p>
                              <p className="text-sm text-slate-500">
                                {new Date(invoice.created).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="text-right">
                              <p className="font-semibold text-slate-900">
                                ${invoice.amount_paid?.toFixed(2) || invoice.amount_due?.toFixed(2)}
                              </p>
                              <Badge className={
                                invoice.status === 'paid' 
                                  ? 'bg-green-100 text-green-700' 
                                  : invoice.status === 'open'
                                  ? 'bg-yellow-100 text-yellow-700'
                                  : 'bg-slate-100 text-slate-700'
                              }>
                                {invoice.status?.charAt(0).toUpperCase() + invoice.status?.slice(1)}
                              </Badge>
                            </div>
                            {invoice.invoice_pdf && (
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => window.open(invoice.invoice_pdf, '_blank')}
                              >
                                <Download className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
            
            <TabsContent value="payment-methods" className="mt-4">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-lg">Payment Methods</CardTitle>
                      <CardDescription>Manage your saved payment methods</CardDescription>
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={openCustomerPortal}
                      disabled={portalLoading}
                    >
                      <Settings className="w-4 h-4 mr-2" />
                      Manage in Portal
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {paymentMethods.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      <CreditCard className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                      <p>No payment methods saved</p>
                      <p className="text-sm mb-4">Add a payment method to manage your subscription</p>
                      <Button onClick={openCustomerPortal} disabled={portalLoading}>
                        {portalLoading ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <CreditCard className="w-4 h-4 mr-2" />
                        )}
                        Add Payment Method
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {paymentMethods.map((pm) => (
                        <div 
                          key={pm.id}
                          className={`flex items-center justify-between p-4 rounded-xl border-2 ${
                            pm.is_default ? 'border-blue-500 bg-blue-50' : 'border-slate-200 bg-slate-50'
                          }`}
                        >
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-8 rounded bg-white border flex items-center justify-center">
                              <span className="text-xs font-bold text-slate-600">{pm.brand}</span>
                            </div>
                            <div>
                              <p className="font-medium text-slate-900">
                                •••• •••• •••• {pm.last4}
                              </p>
                              <p className="text-sm text-slate-500">
                                Expires {pm.exp_month}/{pm.exp_year}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {pm.is_default && (
                              <Badge className="bg-blue-100 text-blue-700">Default</Badge>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        )}

        {/* Plan Comparison */}
        {!subscription?.has_subscription && plans && (
          <Card>
            <CardHeader>
              <CardTitle>Compare Plans</CardTitle>
              <CardDescription>Find the right plan for your business</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-3 gap-4">
                {['starter', 'professional', 'enterprise'].map((tier) => {
                  const plan = plans.plans[tier]?.monthly;
                  if (!plan) return null;
                  
                  const Icon = getPlanIcon(tier + '_monthly');
                  
                  return (
                    <div 
                      key={tier}
                      className={`p-4 rounded-xl border-2 ${
                        plan.popular ? 'border-purple-500 bg-purple-50' : 'border-slate-200'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <Icon className="w-5 h-5 text-slate-600" />
                        <span className="font-semibold">{plan.name.split(' ')[0]}</span>
                        {plan.popular && (
                          <Badge className="bg-purple-100 text-purple-700 text-xs">Popular</Badge>
                        )}
                      </div>
                      <div className="text-2xl font-bold mb-2">${plan.price}<span className="text-sm font-normal text-slate-500">/mo</span></div>
                      <p className="text-sm text-slate-500 mb-3">{plan.features[0]}</p>
                      <Button 
                        size="sm" 
                        variant={plan.popular ? 'default' : 'outline'}
                        className="w-full"
                        onClick={() => navigate('/pricing')}
                      >
                        Get Started
                      </Button>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* FAQ */}
        <Card>
          <CardHeader>
            <CardTitle>Frequently Asked Questions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h4 className="font-medium text-slate-900">What happens when my trial ends?</h4>
              <p className="text-sm text-slate-500 mt-1">
                Your subscription will automatically continue at your selected plan rate. You can cancel anytime before the trial ends.
              </p>
            </div>
            <div>
              <h4 className="font-medium text-slate-900">Can I change plans later?</h4>
              <p className="text-sm text-slate-500 mt-1">
                Yes! You can upgrade or downgrade your plan at any time. Changes take effect on your next billing cycle.
              </p>
            </div>
            <div>
              <h4 className="font-medium text-slate-900">What payment methods do you accept?</h4>
              <p className="text-sm text-slate-500 mt-1">
                We accept all major credit cards (Visa, Mastercard, American Express) through our secure Stripe payment processing.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Cancel Dialog */}
        <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Cancel Subscription</DialogTitle>
              <DialogDescription>
                Are you sure you want to cancel your subscription? You'll lose access to premium features at the end of your current billing period.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCancelDialog(false)}>
                Keep Subscription
              </Button>
              <Button 
                variant="destructive" 
                onClick={handleCancelSubscription}
                disabled={cancelLoading}
              >
                {cancelLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Yes, Cancel
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default BillingPage;
